import React, { useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import { useNavLinksContext } from '../../contexts/NavLinksContext';
import { useAppContext } from '../../contexts/Storage';
import { TripleColumnPage } from '../../components/Templates';
import { useOverallStockValuation } from '../../components/Accounts';
import StockValuationPrintable from '../../components/Accounts/StockValuationPrintable';
import { exportToExcel, ExcelColumnConfig } from '../../utils/excelExport';
import { StockValuationDetailItem } from '../../api/accounts';

const OverallStockValuationPage: React.FC = () => {
  const { setNavLinks } = useNavLinksContext();
  const { selectedRecord } = useAppContext();
  const printableRef = useRef<HTMLDivElement>(null);

  const {
    tripleColumnItems,
    items,
    isLoading,
    hasRequiredParams,
    handleDateFilter,
    filters,
  } = useOverallStockValuation();

  useEffect(() => {
    setNavLinks({
      links: [
        { to: '/dashboard', label: 'Dashboard' },
        { to: '/records', label: 'Records' },
        ...(selectedRecord
          ? [{ to: '/records/accounts/overall-stock-valuation', label: 'Overall Stock Valuation' }]
          : []),
      ],
    });
  }, [setNavLinks, selectedRecord]);

  const captureAsImage = async (): Promise<string | null> => {
    if (!printableRef.current) return null;
    const wrapper = printableRef.current.parentElement;
    if (wrapper) {
      wrapper.style.overflow = 'visible';
      wrapper.style.height = 'auto';
      wrapper.style.position = 'fixed';
      wrapper.style.left = '0';
      wrapper.style.top = '0';
      wrapper.style.zIndex = '-9999';
      wrapper.style.opacity = '1';
    }
    try {
      return await toPng(printableRef.current, {
        backgroundColor: '#ffffff',
        pixelRatio: 2,
      });
    } catch {
      return null;
    } finally {
      if (wrapper) {
        wrapper.style.overflow = 'hidden';
        wrapper.style.height = '0';
        wrapper.style.position = '';
        wrapper.style.left = '';
        wrapper.style.top = '';
        wrapper.style.zIndex = '';
        wrapper.style.opacity = '';
      }
    }
  };

  const handleExportImage = useCallback(async () => {
    const dataUrl = await captureAsImage();
    if (!dataUrl) {
      toast.error('Failed to generate image');
      return;
    }
    const link = document.createElement('a');
    link.download = 'overall_stock_valuation.png';
    link.href = dataUrl;
    link.click();
    toast.success('Image downloaded');
  }, []);

  const handleExportPDF = useCallback(async () => {
    const dataUrl = await captureAsImage();
    if (!dataUrl) {
      toast.error('Failed to generate PDF');
      return;
    }
    const img = new Image();
    img.src = dataUrl;
    await new Promise<void>((resolve) => {
      img.onload = () => resolve();
    });
    const pixelRatio = 2;
    const pxToMm = 0.264583;
    const imgWidthMm = (img.width / pixelRatio) * pxToMm;
    const imgHeightMm = (img.height / pixelRatio) * pxToMm;
    const isLandscape = imgWidthMm > imgHeightMm;
    const pdf = new jsPDF({
      orientation: isLandscape ? 'landscape' : 'portrait',
      unit: 'mm',
      format: [imgWidthMm + 20, imgHeightMm + 20],
    });
    pdf.addImage(dataUrl, 'PNG', 10, 10, imgWidthMm, imgHeightMm);
    pdf.save('overall_stock_valuation.pdf');
    toast.success('PDF downloaded');
  }, []);

    const formatQuantity = (quantity: number) => {
    return quantity.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const handleExportExcel = useCallback(() => {
    if (!items || items.length === 0) {
      toast.error('No data to export');
      return;
    }

    const columns: ExcelColumnConfig<StockValuationDetailItem>[] = [
      {
        key: 'date',
        header: 'Date',
        transform: (v: string) => {
          try {
            return new Date(v).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
          } catch { return v; }
        },
      },
      { key: 'isReceipt', header: 'Type', transform: (v: boolean) => (v ? 'Receipt' : 'Issue') },
            { key: 'quantity', header: 'Quantity', transform: (v: number) => formatQuantity(v) },
      { key: 'rate', header: 'Price', transform: (v: number) => `$${v.toFixed(2)}` },
      { key: 'amount', header: 'Amount', transform: (v: number) => `$${v.toFixed(2)}` },
      { key: 'balanceQuantity', header: 'Balance Qty', transform: (v: number) => formatQuantity(v) },
      { key: 'balanceRate', header: 'Balance Price', transform: (v: number) => `$${v.toFixed(2)}` },
      { key: 'balanceAmount', header: 'Balance Amount', transform: (v: number) => `$${v.toFixed(2)}` },
    ];

    exportToExcel({
      data: items,
      columns,
      fileName: 'overall_stock_valuation',
      sheetName: 'Overall Stock Valuation',
      includeTimestamp: true,
    });
    toast.success('Excel downloaded');
  }, [items]);

  const handlePrint = useCallback(() => {
    if (!printableRef.current) {
      toast.error('Nothing to print');
      return;
    }
    const htmlContent = printableRef.current.outerHTML;
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-9999px';
    iframe.style.top = '-9999px';
    iframe.style.width = '0';
    iframe.style.height = '0';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      toast.error('Failed to prepare print content');
      document.body.removeChild(iframe);
      return;
    }
    doc.open();
    doc.write(`
      <html>
        <head>
          <title>Overall Stock Valuation</title>
          <style>
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          </style>
        </head>
        <body style="margin:0;padding:0;">
          ${htmlContent}
        </body>
      </html>
    `);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 100);
  }, []);

  if (!selectedRecord) {
    return (
      <div className="h-shink-from-navbar h-fill-from-navbar pl-2 bg-bg_grey_light text-dark overflow-y-scroll">
        <div className="bg-light rounded-lg p-8 text-center">
          <p className="text-grey_dark mb-4">Please select a record to view overall stock valuation.</p>
          <Link
            to="/records"
            className="text-primary hover:text-primary_dark underline"
          >
            Go to Records
          </Link>
        </div>
      </div>
    );
  }

  return (
    <TripleColumnPage
      title="Overall Stock Valuation"
      description="Summary of total stock valuation across all categories"
      items={tripleColumnItems}
      loading={isLoading}
      showDateRange={true}
      start_date={filters.start_date?.split(' ')[0]}
      end_date={filters.end_date?.split(' ')[0]}
      onDateRangeChanged={handleDateFilter}
      onExportImage={handleExportImage}
      onExportPDF={handleExportPDF}
      onExportExcel={handleExportExcel}
      onPrint={handlePrint}
      emptyMessage={
        !hasRequiredParams
          ? 'Please select a record to view overall stock valuation.'
          : 'No stock valuation data available.'
      }
      extraContent={
        <div style={{ overflow: 'hidden', height: 0 }}>
          <StockValuationPrintable
            ref={printableRef}
            title="Overall Stock Valuation"
            items={tripleColumnItems}
            startDate={filters.start_date?.split(' ')[0]}
            endDate={filters.end_date?.split(' ')[0]}
          />
        </div>
      }
    />
  );
};

export default OverallStockValuationPage;
